import React, { useState } from "react";
import {
  useAnnouncements,
  useCreateAnnouncement,
  useRemoveAnnouncement,
} from "../hooks/useAnnouncements";

const field =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#013941]";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const AnnouncementsPanel = () => {
  const { data: announcements = [], isLoading } = useAnnouncements();
  const { mutate: create, isPending } = useCreateAnnouncement();
  const { mutate: remove, isPending: isRemoving } = useRemoveAnnouncement();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [path, setPath] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const now = Date.now();
  const isLive = (a) => !a.expiresAt || new Date(a.expiresAt).getTime() > now;

  const submit = (event) => {
    event.preventDefault();
    create(
      {
        title: title.trim(),
        body: body.trim(),
        ...(path.trim() && { data: { path: path.trim() } }),
        ...(expiresAt && { expiresAt: new Date(expiresAt).toISOString() }),
      },
      {
        onSuccess: () => {
          setTitle("");
          setBody("");
          setPath("");
          setExpiresAt("");
        },
      }
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      <form onSubmit={submit} className="space-y-3 rounded-2xl border border-gray-200 p-4">
        <h2 className="text-base font-semibold">New announcement</h2>
        <p className="text-xs text-gray-500">
          Shows in every consumer app user&apos;s notifications for 90 days, or
          until it expires.
        </p>
        <label className="block text-sm">
          Title
          <input
            className={field}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            required
          />
        </label>
        <label className="block text-sm">
          Message
          <textarea
            className={field}
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            required
          />
        </label>
        <label className="block text-sm">
          Open this page when tapped (optional)
          <input
            className={field}
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/rates"
          />
        </label>
        <label className="block text-sm">
          Stop showing on (optional)
          <input
            type="datetime-local"
            className={field}
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={isPending || !title.trim() || !body.trim()}
          className="h-10 w-full rounded-full bg-[#013941] text-sm font-semibold text-white disabled:opacity-50"
        >
          {isPending ? "Publishing..." : "Publish to all users"}
        </button>
      </form>

      <div className="rounded-2xl border border-gray-200">
        <h2 className="border-b border-gray-200 px-4 py-3 text-base font-semibold">
          Published
        </h2>
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading...</p>
        ) : announcements.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">Nothing published yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {announcements.map((a) => (
              <li key={a.uuid} className="flex items-start gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {a.title}{" "}
                    <span
                      className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isLive(a)
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isLive(a) ? "Live" : "Expired"}
                    </span>
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{a.body}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Published {formatDate(a.publishedAt)}
                    {a.expiresAt ? ` · until ${formatDate(a.expiresAt)}` : ""}
                    {a.data?.path ? ` · opens ${a.data.path}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={isRemoving}
                  onClick={() => {
                    if (window.confirm("Remove this announcement for all users?")) {
                      remove(a.uuid);
                    }
                  }}
                  className="shrink-0 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPanel;
